<template>
    <div id="app">
      <main role="main">
        <NavBar/>
        <div class="album main-shell py-2 bg-light">
          <div class="container">
            <router-view @event-loaded="onEventLoaded"/>
          </div>
        </div>
      </main>
      <footer class="footer">
        <div class="container">
          <div class="footer-content">
            <!-- Main footer info -->
            <div class="footer-main">
              <div class="footer-brand">
                <h5 class="footer-title">{{ $t('app.brand') }}</h5>
                <p class="footer-tagline">{{ $t('app.tagline') }}</p>
              </div>

              <!-- Contact Section -->
              <div class="footer-contact-section">
                <h6 class="contact-title">{{ $t('app.contactUs') || 'Contact Us' }}</h6>
                <div class="contact-links">
                  <a href="mailto:42xchallenge.com@proton.me" class="contact-link">
                    <div class="contact-icon-wrapper">
                      <font-awesome-icon icon="envelope" class="contact-icon"/>
                    </div>
                    <div class="contact-info">
                      <span class="contact-label">{{ $t('app.email') || 'Email' }}</span>
                      <span class="contact-value">42xchallenge.com@proton.me</span>
                    </div>
                  </a>
                  <a href="tel:0339844306" class="contact-link">
                    <div class="contact-icon-wrapper">
                      <font-awesome-icon icon="phone" class="contact-icon"/>
                    </div>
                    <div class="contact-info">
                      <span class="contact-label">{{ $t('app.phone') || 'Phone' }}</span>
                      <span class="contact-value">033 984 4306</span>
                    </div>
                  </a>
                </div>
              </div>

              <!-- Quick actions -->
              <div class="footer-actions">
                <router-link class="btn btn-outline-primary btn-sm" to="/" exact>
                  <font-awesome-icon icon="home"/> {{ $t('app.home') }}
                </router-link>
                <a href="#" class="btn btn-outline-secondary btn-sm" @click="scrollToTop">
                  <font-awesome-icon icon="arrow-up"/> {{ $t('app.backToTop') }}
                </a>
              </div>
            </div>

            <!-- Affiliate links -->
            <div class="footer-affiliates">
              <h6 class="affiliate-title">{{ $t('app.affiliateTitle') }}</h6>
              <div class="affiliate-links">
                <a href="https://www.youtube.com/@logauit" target="_blank" class="affiliate-link">
                  <font-awesome-icon icon="video" class="affiliate-icon"/>
                  <span>{{ $t('app.affiliateYoutube') }}</span>
                </a>
                <a href="https://www.42xchallenge.com/" target="_blank" class="affiliate-link">
                  <font-awesome-icon icon="external-link-alt" class="affiliate-icon"/>
                  <span>{{ $t('app.affiliate42x') }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
</template>

<script>
import NavBar from '@/views/NavBar'
export default {
  components: { NavBar },
  watch: {
    '$route' (to) {
      document.title = to.meta.title || 'Keep Running'
      this.updateOpenGraphTags(to.meta)
    }
  },
  methods: {
    scrollToTop () {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    onEventLoaded (event) {
      const eventRoutes = ['eventDetail', 'eventDashboard', 'teamEventDashboard', 'eventVar']
      if (eventRoutes.includes(this.$route.name) && event && event.name) {
        this.updateOpenGraphTags({
          ...this.$route.meta,
          ogDescription: event.name
        })
      }
    },
    updateOpenGraphTags (meta) {
      let ogDescription = meta.ogDescription || meta.title || 'Keep Running'
      const ogTags = {
        'og:title': meta.ogTitle || meta.title || 'Keep Running',
        'og:description': ogDescription,
        'og:image': meta.ogImage || 'https://nghienchaybo.com/default-image.png',
        'og:url': window.location.href,
        'og:type': 'website',
        'og:site_name': 'Nghienchaybo.com',
        'og:locale': 'en_US'
      }
      Object.entries(ogTags).forEach(([property, content]) => {
        let tag = document.querySelector(`meta[property='${property}']`)
        if (!tag) {
          tag = document.createElement('meta')
          tag.setAttribute('property', property)
          document.head.appendChild(tag)
        }
        tag.setAttribute('content', content)
      })
    }
  },
  metaInfo: {
    title: 'Nghienchaybo.com - Keep Running'
  }
}
</script>

<style lang="scss">
  @import '@/styles/sport-theme';

  #app {
      font-family: 'Avenir', Helvetica, Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-align: center;
      color: #1e3a5f;
  }

  main .main-shell {
    padding-top: 0.6rem !important;
    padding-bottom: 0.6rem !important;
  }

  main .album.bg-light {
    background: linear-gradient(180deg, $sport-mist 0%, #ffffff 72%) !important;
  }

  // Footer — compact, minimal chrome
  .footer {
    background: linear-gradient(180deg, $sport-mist 0%, $sport-foam 100%);
    border-top: 1px solid rgba($sport-sky, 0.35);
    padding: 1.25rem 0 1rem;
    margin-top: 2rem;
    position: relative;

    .footer-content {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 1.25rem;
      align-items: start;
      margin-bottom: 0.75rem;
      position: relative;
      z-index: 1;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        text-align: center;
      }
    }

    .footer-main {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;

      @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 1.1rem;
      }

      @media (max-width: 576px) {
        align-items: center;
        gap: 1rem;
      }
    }

    .footer-brand {
      text-align: left;

      @media (max-width: 576px) {
        text-align: center;
      }

      .footer-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: $sport-deep;
        margin-bottom: 0.5rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-shadow: 1px 1px 2px rgba($sport-sky, 0.5);
      }

      .footer-tagline {
        font-size: 0.9rem;
        color: $sport-primary;
        margin-bottom: 0;
        font-weight: 500;
      }

      .footer-powered {
        font-size: 0.8rem;
        color: $sport-primary;
        margin-bottom: 0;
        font-style: italic;
      }
    }

    .footer-contact-section {
      text-align: left;

      @media (max-width: 576px) {
        text-align: center;
        width: 100%;
      }

      .contact-title {
        font-size: 0.9rem;
        font-weight: 600;
        color: $sport-deep;
        margin-bottom: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .contact-links {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .contact-link {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem;
        border-radius: 12px;
        text-decoration: none;
        transition: all 0.3s ease;
        border: 1px solid transparent;
        background: rgba(255, 255, 255, 0.7);

        @media (max-width: 576px) {
          justify-content: center;
        }

        &:hover {
          background: rgba($sport-soft, 0.95);
          border-color: rgba($sport-sky, 0.45);
          text-decoration: none;

          .contact-icon-wrapper {
            background: linear-gradient(135deg, $sport-deep 0%, $sport-primary 100%);

            .contact-icon {
              color: #fff;
            }
          }

          .contact-value {
            color: $sport-deep;
          }
        }
      }

      .contact-icon-wrapper {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: linear-gradient(135deg, $sport-glow 0%, $sport-sky 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background 0.2s ease, border-color 0.2s ease;
        border: 1px solid rgba($sport-deep, 0.2);

        .contact-icon {
          font-size: 0.9rem;
          color: $sport-deep;
          transition: all 0.3s ease;
        }
      }

      .contact-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.1rem;

        @media (max-width: 576px) {
          align-items: center;
        }
      }

      .contact-label {
        font-size: 0.75rem;
        color: $sport-primary;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .contact-value {
        font-size: 0.9rem;
        color: $sport-primary;
        font-weight: 600;
        transition: all 0.3s ease;
      }
    }

    .footer-actions {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;

      .btn {
        border-radius: 8px;
        padding: 0.35rem 0.85rem;
        font-size: 0.8125rem;
        font-weight: 600;
        transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

        &.btn-outline-primary {
          border: 1px solid $sport-deep;
          color: $sport-deep;
          background: rgba(255, 255, 255, 0.85);

          &:hover {
            background: $sport-deep;
            color: #fff;
            border-color: $sport-deep;
          }
        }

        &.btn-outline-secondary {
          border: 1px solid rgba($sport-sky, 0.75);
          color: $sport-primary;
          background: rgba(255, 255, 255, 0.85);

          &:hover {
            background: rgba($sport-soft, 0.9);
            color: $sport-deep;
            border-color: $sport-sky;
          }
        }

        .fa-icon {
          margin-right: 0.375rem;
        }
      }
    }

    .footer-affiliates {
      text-align: right;
      background: rgba(255, 255, 255, 0.6);
      padding: 0.75rem 0.85rem;
      border-radius: 8px;
      border: 1px solid rgba($sport-sky, 0.35);

      @media (max-width: 768px) {
        text-align: center;
      }

      .affiliate-title {
        font-size: 0.9rem;
        font-weight: 700;
        color: $sport-deep;
        margin-bottom: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .affiliate-links {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .affiliate-link {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5rem;
        color: $sport-primary;
        text-decoration: none;
        font-size: 0.875rem;
        padding: 0.35rem 0.5rem;
        transition: all 0.3s ease-in-out;
        border-radius: 8px;

        @media (max-width: 768px) {
          justify-content: center;
        }

        &:hover {
          color: $sport-deep;
          text-decoration: none;
          background: rgba($sport-soft, 0.75);
          padding: 0.35rem 0.5rem;
        }

        .affiliate-icon {
          font-size: 0.85rem;
          color: $sport-sky;
        }

        span {
          font-weight: 600;
        }
      }
    }

    .footer-bottom {
      .footer-divider {
        border: none;
        height: 1px;
        background: rgba($sport-sky, 0.35);
        margin: 0.65rem 0;
      }

      .copyright {
        font-size: 0.8rem;
        color: $sport-primary;
        margin-bottom: 0;
        text-align: center;
      }
    }
  }

  // Dark mode — cool sport tones
  @media (prefers-color-scheme: dark) {
    .footer {
      background: linear-gradient(135deg, #0f172a 0%, #172554 45%, #0c4a6e 100%);
      border-top-color: rgba($sport-sky, 0.45);

      .footer-title {
        color: $sport-glow;
      }

      .footer-tagline {
        color: $sport-soft;
      }

      .footer-powered {
        color: $sport-primary;
      }

      .contact-title {
        color: $sport-glow;
      }

      .contact-link {
        background: rgba(0, 0, 0, 0.3);

        &:hover {
          background: linear-gradient(135deg, rgba($sport-deep, 0.3), rgba($sport-sky, 0.2));
          border-color: $sport-sky;
        }
      }

      .contact-icon-wrapper {
        background: linear-gradient(135deg, $sport-deep 0%, $sport-primary 100%);
        border-color: $sport-sky;

        .contact-icon {
          color: $sport-glow;
        }
      }

      .contact-label {
        color: $sport-sky;
      }

      .contact-value {
        color: $sport-soft;
      }

      .footer-actions {
        .btn {
          &.btn-outline-primary {
            border-color: $sport-sky;
            color: $sport-glow;
            background: rgba(0, 0, 0, 0.3);

            &:hover {
              background: linear-gradient(135deg, $sport-sky, $sport-glow);
              color: $sport-deep;
            }
          }

          &.btn-outline-secondary {
            border-color: $sport-soft;
            color: $sport-soft;
            background: rgba(0, 0, 0, 0.3);

            &:hover {
              background: linear-gradient(135deg, $sport-soft, $sport-aqua);
              color: $sport-deep;
            }
          }
        }
      }

      .footer-affiliates {
        background: rgba(0, 0, 0, 0.3);
        border-color: rgba($sport-sky, 0.4);

        .affiliate-title {
          color: $sport-glow;
        }

        .affiliate-link {
          color: $sport-soft;

          &:hover {
            color: $sport-glow;
            background: linear-gradient(135deg, rgba($sport-deep, 0.2), rgba($sport-sky, 0.2));
          }

          .affiliate-icon {
            color: $sport-sky;
          }
        }
      }

      .footer-divider {
        background: rgba($sport-soft, 0.25);
      }

      .copyright {
        color: $sport-sky;
      }
    }
  }
</style>
